/**
 * OCT20 WS-ROLL (Issue 04) — a continuation day shows the repair's jobs.
 *
 * A rollover chain A → B → C is ONE repair whose job lines live on its head A (20270114100000: the job owner); the
 * continuation days carry no job lines of their own, so their Dayboard card read "Jobs:" with nothing under it
 * (Floyd's, Monday TKT-0018). This lists the OWNER's lines, read-only and labelled with where they live. It changes
 * no ownership and no completion semantics: the lines stay on the head, a non-final day still completes only its day,
 * and every job action keeps going through the existing authorities.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface OwnerJobLine {
  jobId: string;
  name: string;
  status: string | null;
}

export interface OwnerJobs {
  ownerTicketId: string;
  ownerTicketNumber: number | null;
  lines: OwnerJobLine[];
}

type LooseDb = {
  rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }>;
  from: (t: string) => any;
};

/** Reads the head's lines for a continuation. null = not a continuation of another ticket, or unreadable. */
export async function loadContinuationOwnerJobs(ticketId: string, db: LooseDb = supabase as unknown as LooseDb): Promise<OwnerJobs | null> {
  const { data: owner, error } = await db.rpc('rollover_job_owner', { p_ticket_id: ticketId });
  if (error || typeof owner !== 'string' || !owner || owner === ticketId) return null;
  const [{ data: lines }, { data: head }] = await Promise.all([
    db.from('ticket_jobs').select('job_id, job_status, jobs!ticket_jobs_job_id_fkey ( id, name )').eq('ticket_id', owner),
    db.from('tickets').select('simple_ticket_number').eq('id', owner).maybeSingle(),
  ]);
  return {
    ownerTicketId: owner,
    ownerTicketNumber: (head as { simple_ticket_number?: number | null } | null)?.simple_ticket_number ?? null,
    lines: ((lines ?? []) as Array<{ job_id: string; job_status: string | null; jobs: { name?: string } | null }>).map((l) => ({
      jobId: l.job_id, name: l.jobs?.name ?? 'Unknown Job', status: l.job_status ?? null,
    })),
  };
}

export function ownerJobsHeading(o: Pick<OwnerJobs, 'ownerTicketId' | 'ownerTicketNumber'>): string {
  return `Repair jobs (from ${o.ownerTicketNumber != null ? `#TKT-${String(o.ownerTicketNumber).padStart(4, '0')}` : o.ownerTicketId})`;
}

const DONE = new Set(['completed', 'skipped', 'declined']);

export function ContinuationRepairJobs({ ticketId }: { ticketId: string }) {
  const [owner, setOwner] = useState<OwnerJobs | null>(null);
  useEffect(() => {
    let cancelled = false;
    void loadContinuationOwnerJobs(ticketId).then((o) => { if (!cancelled) setOwner(o); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [ticketId]);
  if (!owner || owner.lines.length === 0) return null;
  return (
    <div data-testid="continuation-repair-jobs">
      <p className="text-xs text-muted-foreground mb-1">{ownerJobsHeading(owner)}:</p>
      {owner.lines.map((l) => (
        <div key={l.jobId} className={`text-sm ${l.status && DONE.has(l.status) ? 'line-through text-muted-foreground' : ''}`}
          data-testid="continuation-repair-job">
          {l.name}
        </div>
      ))}
    </div>
  );
}
